import { PropertyItem } from "@/shared/components/views/property-item.model";
import { Translation } from "@/shared/data-display-config/composable/data-display-config.ts";
import {
    DataDisplayConfigQuery,
    DatafieldType,
    DateInclusiveRange,
    FieldDefinitionTypes,
    LongInclusiveRange,
} from "@/shared/services/graphql/generated/consumer-graph-types";
import { formateDate } from "@/shared/time/convert";
import { DataDisplayField, DatafieldFragments, DisplayResultNode } from "./data-display.model";
import { isLinkDisplayField } from "@/shared/data-display-config/data-display.utils.ts";
import { useLanguageStore } from "@/shared/store/language-store.ts";

export type Symbols = Array<{ icon: string; label: string | undefined }> | undefined;

const EN_DASH = "–";

export const getDatafieldsFragmentsFromConfig = (config: DataDisplayConfigQuery): DatafieldFragments => {
    const datafields: Record<string, string[]> = {};
    const localizationDatafields: Record<string, string[]> = {};

    const addFragmentToDatafields = (
        datafieldType: Record<string, string[]>,
        objectTypes: string[] | null,
        fragment: string
    ) => {
        if (objectTypes === null) {
            if (datafieldType["all"]) {
                datafieldType["all"].push(fragment);
            } else {
                datafieldType["all"] = [fragment];
            }
        } else {
            for (const objectType of objectTypes) {
                if (datafieldType[objectType]) {
                    datafieldType[objectType].push(fragment);
                } else {
                    datafieldType[objectType] = [fragment];
                }
            }
        }
    };

    config.dataDisplayConfig?.entries?.forEach((entry) => {
        if (entry.type !== DatafieldType.metadata) return;

        const isDataDisplayConfig = entry.__typename === "DataDisplayConfigDatafieldEntry";
        const objectTypes = isDataDisplayConfig
            ? ((entry?.datafieldDefinition?.objectTypes as string[]) ?? null)
            : null;

        let fragment: string;

        switch (entry.fieldType) {
            case FieldDefinitionTypes.text:
                fragment = `${entry.referencedId}(acceptedLanguages: $acceptedLanguages)`;
                addFragmentToDatafields(localizationDatafields, objectTypes, fragment);
                break;

            case FieldDefinitionTypes.dateTimeRange:
            case FieldDefinitionTypes.numberRange:
                fragment = `${entry.referencedId}{gte lte}`;
                addFragmentToDatafields(datafields, objectTypes, fragment);
                break;

            default:
                fragment = entry.referencedId;
                addFragmentToDatafields(datafields, objectTypes, fragment);
                break;
        }
    });

    return {
        datafields,
        localizationDatafields,
    };
};

export const getDataDisplayFields = (
    config: DataDisplayConfigQuery | undefined,
    t: Translation
): Array<DataDisplayField> => {
    if (!config?.dataDisplayConfig?.entries?.length) return [];

    return config.dataDisplayConfig.entries.map((entry) => {
        const displayValues: Record<string, string> = {};
        let trueValue: undefined | string = undefined;
        let falseValue: undefined | string = undefined;

        if (
            entry.datafieldDefinition?.__typename === "TaxonomyDatafieldDefinition" ||
            entry.datafieldDefinition?.__typename === "EnumDatafieldDefinition"
        ) {
            if (entry.datafieldDefinition.values && entry.datafieldDefinition.values.length > 0) {
                for (const x of entry.datafieldDefinition.values) {
                    const referencedId = x.key;
                    displayValues[referencedId] = x.teasers?.title ?? referencedId;
                }
            }
        } else if (entry.datafieldDefinition?.__typename === "BooleanDatafieldDefinition") {
            trueValue = entry.datafieldDefinition.trueValue?.teasers?.title ?? t("shared.Yes");
            falseValue = entry.datafieldDefinition.falseValue?.teasers?.title ?? t("shared.No");
        }

        const isPriority = entry.displayOptions?.showMobile === true;

        const property: DataDisplayField = {
            key: entry.referencedId,
            title: entry.teasers?.title ?? entry.referencedId,
            fieldTypeInfo: {
                type: entry.type,
                fieldType: entry.fieldType,
                displayOptions: entry.displayOptions,
            },
            displayValues,
            isPriority,
            trueValue,
            falseValue,
        };

        return property;
    });
};

export const getPriorityDataDisplayFields = (config: DataDisplayConfigQuery | undefined, t: Translation) => {
    return getDataDisplayFields(config, t).sort((x, y) => {
        return x.isPriority === y.isPriority ? 0 : x.isPriority ? -1 : 1;
    });
};

export const getNodeDataFragments = (config: DataDisplayConfigQuery) => {
    //fields, which are only valid for topics
    //fields, which are only valid for contentMaps
    //workaround for field title
    const results: Record<string, string> = {};
    let topicFields = "";
    let contentMapFields = "";
    const entries = config.dataDisplayConfig?.entries;

    const dataDisplayConfigFields =
        entries
            ?.filter((val) => val.type === DatafieldType.field)
            ?.filter((x) => {
                return x.referencedId !== "title";
            }) || [];

    const dataDisplayConfigTopicFields = dataDisplayConfigFields?.filter((x) => {
        if (x.__typename === "DataDisplayConfigFieldEntry") return x?.nodeTypes?.includes("topic");
    });

    const dataDisplayConfigContentMapFields = dataDisplayConfigFields?.filter((x) => {
        if (x.__typename === "DataDisplayConfigFieldEntry") return x.nodeTypes?.includes("contentMap");
    });

    if (dataDisplayConfigFields?.length) {
        for (const field of dataDisplayConfigTopicFields) {
            topicFields += `${field.referencedId}\n`;
        }

        for (const field of dataDisplayConfigContentMapFields) {
            contentMapFields += `${field.referencedId}\n`;
        }
    }

    results.topic = topicFields;
    results.contentMap = contentMapFields;

    return results;
};

const toArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : [value]);

export const getDataDisplayValue = (
    field: DataDisplayField,
    graphqlResultObject: unknown
): { formattedValue: string; symbols?: Symbols; exists: boolean; colorClass?: string } => {
    const locale = useLanguageStore().uiLocale;

    if (!graphqlResultObject || typeof graphqlResultObject !== "object") {
        return { exists: false, formattedValue: "" };
    }

    const resultNode = graphqlResultObject as DisplayResultNode;
    let displayValues: unknown;
    let symbols: Symbols;
    const fieldValue = resultNode[field.key] ?? resultNode.useContext?.[field.key];

    const displayOptions = field.fieldTypeInfo.displayOptions;
    const symbolConfigurationEnabled = displayOptions?.symbolConfigurationEnabled;

    const getLabelByLanguage = (label: Record<string, string> | undefined) => {
        return label?.[locale]?.length ? label[locale] : label?.["en"];
    };

    switch (field.fieldTypeInfo.fieldType) {
        case FieldDefinitionTypes.taxonomy:
        case FieldDefinitionTypes.enum:
            if (fieldValue) {
                const valuesArray = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
                if (symbolConfigurationEnabled) {
                    symbols = valuesArray.map((value) => {
                        const symbolConfigLabel = displayOptions?.symbolConfigLabel?.[value];

                        //if symbol configuration is enabled, but no symbol is configured (undefined or ""), show "empty"
                        //we set "empty" as a placeholder for this case, so no font-awesome icon is shown, but we still have a value
                        //this is necessary to distinguish between the case where symbol configuration is enabled but no symbol is configured (show empty) and the case where symbol configuration is not enabled at all (show value without symbol)

                        return {
                            icon: displayOptions.symbolConfig?.[value] || "empty",
                            label: getLabelByLanguage(symbolConfigLabel),
                        };
                    });
                }

                displayValues = valuesArray.map((value) => field.displayValues[value] || value);
            }
            break;
        case FieldDefinitionTypes.text:
            if (resultNode.teasers) {
                displayValues = resultNode.teasers[field.key];
            } else if (resultNode.localizations) {
                displayValues = resultNode.localizations[field.key];
            }
            break;
        case FieldDefinitionTypes.dateTime:
        case FieldDefinitionTypes.date:
            if (fieldValue) {
                const isDateTime =
                    field.fieldTypeInfo.fieldType === FieldDefinitionTypes.date
                        ? false
                        : field.fieldTypeInfo.displayOptions?.dateOptions !== "date";
                displayValues = Array.isArray(fieldValue)
                    ? fieldValue.map((val: string) => formateDate(val, locale, isDateTime))
                    : formateDate(String(fieldValue), locale, isDateTime);
            }
            break;
        case FieldDefinitionTypes.dateTimeRange:
            if (fieldValue) {
                const isDateTime = field.fieldTypeInfo.displayOptions?.dateOptions !== "date";
                const values = toArray(fieldValue) as DateInclusiveRange[];

                displayValues = values.map(({ gte, lte }) => {
                    const from = gte ? formateDate(gte, locale, isDateTime) : "";
                    const to = lte ? formateDate(lte, locale, isDateTime) : "";
                    return `${from} ${EN_DASH} ${to}`;
                });
            }
            break;
        case FieldDefinitionTypes.numberRange:
            if (fieldValue) {
                const values = toArray(fieldValue) as LongInclusiveRange[];
                displayValues = values.map(({ gte, lte }) => {
                    return `${gte ?? ""} ${EN_DASH} ${lte ?? ""}`;
                });
            }
            break;
        case FieldDefinitionTypes.boolean:
            if (fieldValue !== undefined && fieldValue !== null) {
                const values = toArray(fieldValue);
                const trueLabel = field.trueValue;
                const falseLabel = field.falseValue;
                if (symbolConfigurationEnabled) {
                    symbols = values.map((value) => {
                        const symbolConfigLabel = displayOptions?.symbolConfigLabel?.[value ? "true" : "false"];

                        //if symbol configuration is enabled, but no symbol is configured (undefined or ""), show "empty"
                        //we set "empty" as a placeholder for this case, so no font-awesome icon is shown, but we still have a value
                        //this is necessary to distinguish between the case where symbol configuration is enabled but no symbol is configured (show empty) and the case where symbol configuration is not enabled at all (show value without symbol)

                        return {
                            icon: displayOptions.symbolConfig?.[value ? "true" : "false"] || "empty",
                            label: getLabelByLanguage(symbolConfigLabel),
                        };
                    });
                }

                displayValues = values.map((value) => (value ? trueLabel : falseLabel));
            }
            break;

        default:
            displayValues = fieldValue;
            break;
    }

    const formattedValue = Array.isArray(displayValues) ? displayValues.join(", ") : String(displayValues ?? "");

    const exists = Array.isArray(displayValues) ? displayValues.length > 0 : !!displayValues;
    // If symbol configuration is enabled, check if symbols array has valid values
    const hasValidSymbols = symbolConfigurationEnabled
        ? !!(symbols && symbols.length > 0 && symbols.some((s) => s.icon !== ""))
        : true;

    const actualExists = exists && hasValidSymbols;

    //TODO: Bis in QIT-1139 geklärt ist, ob '' ein zulässiger Wert ist und ob im Import leer Strings als undefined abgespeichert werden hier alles leere als nicht existierend setzen
    return {
        exists: actualExists,
        formattedValue,
        symbols,
        colorClass: displayOptions?.colorClass,
    };
};

export const getDataDisplayPropertyItems = (fields: Array<DataDisplayField>, graphqlResultObject: unknown) => {
    const result: Array<PropertyItem> = [];
    if (graphqlResultObject) {
        fields.forEach((field) => {
            const { exists, symbols, formattedValue } = getDataDisplayValue(field, graphqlResultObject);
            const isLink = isLinkDisplayField(field.fieldTypeInfo?.fieldType, formattedValue);

            if (exists) {
                const property: PropertyItem = {
                    key: field.key,
                    title: field.title,
                    value: formattedValue,
                    fieldType: (field.fieldTypeInfo?.fieldType as string) ?? undefined,
                    isPriority: field.isPriority,
                    displayOptions: field.fieldTypeInfo.displayOptions,
                    symbols,
                    isLink,
                };

                result.push(property);
            }
        });
    }

    return result;
};
